import {
	addAmount,
	divideAmount,
	floorAmount,
	multiplyAmount,
} from "@/lib/utils"
import { getCurrentLanguageInClient } from "@/lib/utils.client"
import type {
	AppliedCouponData,
	CartItem,
} from "../../checkout/types"
import type { UserCouponWithTemplate } from "../schema"
import { isCouponApplicableForWeb } from "./coupon-validator"

export function calculateDiscountAmount(
	coupon: UserCouponWithTemplate,
	cartItems: CartItem[],
): number {
	if (
		coupon.couponType === "discount" &&
		coupon.appliedDiscountRule?.discountRate
	) {
		// 使用 Decimal.js 进行精确计算
		const cartTotal = cartItems.reduce(
			(sum, item) => addAmount(sum, multiplyAmount(item.price, item.count)),
			0,
		)
		const discountRate = divideAmount(
			coupon.appliedDiscountRule.discountRate,
			100,
		)
		const discountAmount = multiplyAmount(cartTotal, discountRate)
		return floorAmount(discountAmount)
	}

	if (
		coupon.couponType === "full_reduction" &&
		coupon.appliedDiscountRule?.reductionAmount
	) {
		return coupon.appliedDiscountRule.reductionAmount
	}

	return 0
}

export function transformUserCouponToApplied(
	coupon: UserCouponWithTemplate,
	cartItems: CartItem[],
): AppliedCouponData {
	const discountAmount = calculateDiscountAmount(coupon, cartItems)
	const lang = getCurrentLanguageInClient()

	if (!coupon.couponTemplate) {
		throw new Error("Coupon template is required for transformation")
	}

	return {
		couponTemplate: {
			id: coupon.couponTemplate.id,
			name: coupon.couponTemplate.title_trans?.[lang] || "Unknown",
			description: coupon.couponTemplate.description_trans?.[lang],
			discountType: coupon.couponTemplate.type,
			discountValue:
				coupon.couponTemplate.type === "discount"
					? coupon.appliedDiscountRule?.discountRate || 0
					: coupon.appliedDiscountRule?.reductionAmount || 0,
			minimumAmount: coupon.appliedDiscountRule?.minAmount,
			validFrom: new Date(coupon.couponTemplate.startTime || new Date()),
			validTo: new Date(coupon.couponTemplate.endTime || new Date()),
		},
		discountAmount,
		applicableProductSpuIds: [],
		canClaim: true,
		userCouponId: coupon.id,
	}
}

/**
 * Check if web coupon is available (web platform + meets minimum order requirement + has started)
 * Used for counting available coupons and determining if they should be greyed out
 */
export function isWebCouponAvailable(
	coupon: UserCouponWithTemplate,
	cartSubtotal: number,
): boolean {
	// First check if it's applicable for web platform
	if (!isCouponApplicableForWeb(coupon)) {
		return false
	}

	// Check if coupon has started
	if (coupon.couponTemplate?.startTime) {
		const startTime = new Date(coupon.couponTemplate.startTime)
		const now = new Date()
		if (startTime > now) {
			return false // Coupon hasn't started yet
		}
	}

	// Then check minimum order amount requirement
	const minAmount = coupon.appliedDiscountRule?.minAmount
	if (!minAmount || minAmount <= 0) {
		return true // No minimum requirement
	}
	return cartSubtotal >= minAmount
}
